/**
 * Margin strip line items — must sum to projected_cost (engine truth).
 * Engine: sum(max(committed, invoiced) per sub) + sum(job_costs).
 */
import { formatMoney } from './formatters'
import { isOverheadCostDescription } from './costsMarginOverhead'

export type StripCommitment = {
  company_name?: string | null
  trade?: string | null
  committed_cents?: number
  invoiced_cents?: number
}

export type StripJobCost = {
  description: string
  amount_cents: number
  category?: string
}

export type MarginStripLine = {
  id: string
  label: string
  amount_cents: number
  kind: 'sub' | 'overhead' | 'direct'
}

export function costContributionCents(committedCents: number, invoicedCents: number): number {
  return Math.max(committedCents || 0, invoicedCents || 0)
}

export function subStripLabel(c: StripCommitment): string {
  const name =
    (c.company_name && String(c.company_name).trim())
    || (c.trade && String(c.trade).trim())
    || 'Sub'
  const committed = c.committed_cents || 0
  const invoiced = c.invoiced_cents || 0
  if (invoiced > committed) {
    return `${name} — invoiced ${formatMoney(invoiced / 100)} (exceeds Subcontract Amount ${formatMoney(committed / 100)})`
  }
  return `${name} — Subcontract Amount ${formatMoney(committed / 100)}`
}

/**
 * Deduction display: never "-$0.00" — zero prints "$0.00".
 */
export function formatDeductionCents(cents: number): string {
  const n = parseInt(String(cents), 10) || 0
  if (n === 0) return formatMoney(0)
  const abs = formatMoney(Math.abs(n) / 100)
  return n < 0 ? abs : `−${abs}`
}

export function buildMarginStripLines(
  commitments: StripCommitment[],
  jobCosts: StripJobCost[],
): MarginStripLine[] {
  const lines: MarginStripLine[] = []
  commitments.forEach((c, i) => {
    const amount_cents = costContributionCents(c.committed_cents || 0, c.invoiced_cents || 0)
    lines.push({
      id: `sub-${i}`,
      label: subStripLabel(c),
      amount_cents,
      kind: 'sub',
    })
  })
  const overheadCents = jobCosts
    .filter((r) => isOverheadCostDescription(r.description))
    .reduce((s, r) => s + (r.amount_cents || 0), 0)
  const directCents = jobCosts
    .filter((r) => !isOverheadCostDescription(r.description))
    .reduce((s, r) => s + (r.amount_cents || 0), 0)
  lines.push({ id: 'overhead', label: 'Overhead / general conditions', amount_cents: overheadCents, kind: 'overhead' })
  lines.push({ id: 'direct', label: 'Direct costs', amount_cents: directCents, kind: 'direct' })
  return lines
}

export function stripLinesSumCents(lines: MarginStripLine[]): number {
  return lines.reduce((s, l) => s + (l.amount_cents || 0), 0)
}
